import { useUpdateAssetsMutation } from "@/hooks/assets";
import { useCurrentUploadId } from "@/hooks/uploads";
import { useStore } from "@/services/store";
import {
  AssetsChangeMap,
  AssetState,
  ValidationEntry,
  isAssetValidationEntry,
} from "@/services/store/slices/mandatoryMetaData/types";
import { AssetDto, UpdateAssetDto } from "generated";
import { useShallow } from "zustand/react/shallow";
import { useLocation, useNavigate, useParams } from "react-router";

type UpdateMetadata = keyof UpdateAssetDto | "comment";

const getValidationProps = (
  validationMessages: { field: string; message: string }[] | undefined,
  field: string,
) => {
  return {
    shouldValidate: validationMessages?.some((v) => v.field === field) ?? false,
    validation: {
      msg: validationMessages?.find((v) => v.field === field)?.message,
    },
  };
};

export function useDocumentValidation(assetId: number, field: string) {
  const validationMessages = useStore(
    (state) => state.documentAssets[assetId]?.validationMessage,
  );
  return getValidationProps(validationMessages, field);
}

export function useMandatoryMediaNavigation(
  validateFn: () => Promise<{ data: boolean | undefined }>,
  isLockedForOwner: boolean,
) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { assetIds } = useParams();
  const uploadId = useCurrentUploadId();
  const isAdminFlow = pathname.includes("/admin");

  const onPrev = () => {
    if (isAdminFlow) {
      navigate(`/admin/${uploadId}`);
    } else if (isLockedForOwner) {
      navigate(`/uploads/${uploadId}/comments`);
    } else {
      navigate(`/uploads/${uploadId}/confirm-files`);
    }
  };

  const onContinue = async () => {
    const { data } = await validateFn();
    if (!data) return;

    if (isAdminFlow) {
      navigate(
        `/admin/${uploadId}/edit/mandatory-metadata${assetIds ? `/${assetIds}` : ""}`,
      );
    } else {
      navigate(`/uploads/${uploadId}/mandatory-metadata`);
    }
  };

  return { onPrev, onContinue, isAdminFlow };
}

export const useMetadataFields = () => {
  const { assets, validationErrors } = useStore(
    useShallow((state) => ({
      assets: state.assets,
      validationErrors: state.validationErrors,
    })),
  );

  const updateAssetsMutation = useUpdateAssetsMutation();

  const updateMetadata = async (update: UpdateAssetDto) => {
    await updateAssetsMutation.mutateAsync(
      assets
        .filter((asset) => asset.isChecked)
        .map((asset) => asset.id!)
        .reduce((acc, assetId) => {
          acc[assetId] = update;
          return acc;
        }, {} as AssetsChangeMap),
    );
  };

  const getValidationError = (
    field: keyof AssetDto,
    assetId: number,
  ): ValidationEntry | undefined => {
    return validationErrors.find(
      (error) =>
        isAssetValidationEntry(error) &&
        error.field === field &&
        error.assetId === assetId,
    );
  };

  const hasAssetValidationErrors = (assetId: number): boolean => {
    return validationErrors.some(
      (error) => isAssetValidationEntry(error) && error.assetId === assetId,
    );
  };

  const activeAsset =
    assets.find((asset) => asset.isActive) || ({} as AssetState);

  const isMetadataUpdating = (
    assetId: number,
    metadata: UpdateMetadata,
  ): boolean => {
    const updating = updateAssetsMutation.updatingMetadata?.[assetId];
    return updating ? Boolean(updating[metadata]) : false;
  };

  return {
    updateMetadata,
    getValidationError,
    hasAssetValidationErrors,
    activeAsset,
    isMetadataUpdating,
  };
};
